import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { AlertTriangle, CheckCircle, FileUp, Loader2, Rocket, RefreshCw, ShieldCheck } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import { Card, EmptyState, PageLoader } from '../ui';
import ConfirmDialog from '../common/ConfirmDialog';
import permissionsService from '../../services/permissionsService';
import {
  PROMOTE_STUDY_BUILD_PERMISSION,
  deployPackage,
  getDeploymentHistory,
  preflightPackage,
  readErrorMessage,
  verifyDeployment,
} from '../../services/studyPromotionApi';
import PreflightReportView from './PreflightReportView';
import VerificationReportView from './VerificationReportView';

const STATUS_BADGES = {
  deployed: 'badge-success',
  failed: 'badge-danger',
  noop: 'badge-gray',
};

// FECOA-1920. "Not verified" is deliberately neutral rather than a warning: a
// deploy nobody has checked yet is not the same as one that failed its check.
const VERIFICATION_BADGES = {
  match: 'badge-success',
  mismatch: 'badge-danger',
  unverifiable: 'badge-warning',
};

const VERIFICATION_LABELS = {
  match: 'Verified',
  mismatch: 'Drifted',
  unverifiable: 'Unverifiable',
};

const formatTimestamp = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const StudyPromotionMain = () => {
  const navigate = useNavigate();

  const [hasAccess, setHasAccess] = useState(null);
  const [target, setTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [packagePayload, setPackagePayload] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [openVerification, setOpenVerification] = useState(null);

  useEffect(() => {
    permissionsService
      .hasPermission(PROMOTE_STUDY_BUILD_PERMISSION)
      .then(setHasAccess)
      .catch(() => setHasAccess(false));
  }, []);

  useEffect(() => {
    if (hasAccess === false) {
      const timer = setTimeout(() => {
        navigate('/studies', {
          state: {
            accessDenied: true,
            message: 'You do not have permission to promote study builds.',
          },
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasAccess, navigate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await getDeploymentHistory();
      setHistory(data.deployments || []);
      setTarget(data.target || null);
    } catch (err) {
      console.error('Failed to load deployment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) loadHistory();
  }, [hasAccess, loadHistory]);

  // FECOA-1920. Re-verify on demand: a build that was green at deploy time can
  // drift later, so this is a check the operator can repeat, not a one-off.
  const runVerification = useCallback(async (deploymentId) => {
    setVerifyingId(deploymentId);
    try {
      const data = await verifyDeployment(deploymentId);
      const verification = data.verification;
      setOpenVerification({ deploymentId, verification });
      setHistory((rows) => rows.map((row) => (
        row.id === deploymentId ? { ...row, latest_verification: verification } : row
      )));
      if (verification.ok) {
        toast.success('Target matches the candidate on every section');
      } else {
        toast.error(verification.detail || 'Verification did not pass');
      }
    } catch (err) {
      toast.error(await readErrorMessage(err, 'Verification failed'));
    } finally {
      setVerifyingId(null);
    }
  }, []);

  const runPreflight = async (file) => {
    setSelectedFile(file);
    setPackagePayload(null);
    setPreflight(null);
    setDeployResult(null);
    setPreflightLoading(true);
    try {
      const data = await preflightPackage(file);
      setPreflight(data);
      setTarget(data.target || null);
      // Parse the package locally too, for the deep planned-changes review.
      // The server report stays the authority; this is display only.
      try {
        setPackagePayload(JSON.parse(await file.text()).payload || null);
      } catch {
        setPackagePayload(null);
      }
    } catch (err) {
      setSelectedFile(null);
      toast.error(await readErrorMessage(err, 'That file is not a valid candidate package.'));
    } finally {
      setPreflightLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) runPreflight(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'] },
    multiple: false,
  });

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const result = await deployPackage(selectedFile);
      setDeployResult(result);
      setConfirmOpen(false);
      if (result.status === 'noop') {
        toast.error(result.message || 'This candidate is already deployed; no changes were made.');
      } else {
        toast.success(
          result.status === 'replaced'
            ? `Candidate deployed — the previous build of ${result.study.title} was replaced.`
            : `Candidate deployed — ${result.study.title} was created in this environment.`
        );
      }
      loadHistory();
    } catch (err) {
      setConfirmOpen(false);
      const data = err?.response?.data;
      if (data?.report) {
        setPreflight((prev) => (prev ? { ...prev, report: data.report } : prev));
      }
      toast.error(data?.error || 'Deploy failed. Nothing was written.');
      loadHistory();
    } finally {
      setDeploying(false);
    }
  };

  if (hasAccess === null) {
    return <PageLoader />;
  }

  if (!hasAccess) {
    return (
      <Card className="mx-auto mt-10 max-w-md">
        <EmptyState
          icon={AlertTriangle}
          tone="danger"
          title="Access Denied"
          description="You do not have permission to promote study builds."
          action={
            <button onClick={() => navigate('/studies')} className="btn btn-primary">
              Return to Studies
            </button>
          }
        />
      </Card>
    );
  }

  const manifest = preflight?.manifest;
  const report = preflight?.report;

  return (
    <div>
        <PageHeader
          title="Study Promotion"
          titleAccessory={target?.environment ? <span className="badge badge-gray">{target.environment}</span> : null}
          subtitle={
            target
              ? `Deploy a verified candidate build into ${target.environment} (v${target.application_version})`
              : 'Deploy a verified candidate build into this environment'
          }
        />

        {/* Upload */}
        <div className="card mb-5 p-5">
          <div
            {...getRootProps()}
            className={`cursor-pointer rounded-lg border border-dashed px-6 py-10 text-center transition-colors ${
              isDragActive
                ? 'border-primary-600 bg-primary-50 dark:border-primary-400 dark:bg-primary-400/10'
                : 'border-gray-300 bg-gray-50/60 hover:border-primary-500 hover:bg-primary-50/40 dark:border-white/15 dark:bg-white/[0.02] dark:hover:border-primary-400 dark:hover:bg-primary-400/[0.06]'
            }`}
          >
            <input {...getInputProps()} />
            <FileUp className="mx-auto mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
            <p className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
              {selectedFile ? selectedFile.name : 'Drop a candidate package here, or click to browse'}
            </p>
            <p className="mt-1 text-[12.5px] text-gray-500 dark:text-gray-400">
              <span className="font-mono">.fxbuild.json</span>, exported from the source environment's study page
            </p>
          </div>
          {preflightLoading && (
            <div className="mt-4 flex items-center justify-center gap-2 text-[13px] text-gray-600 dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
              Running preflight checks...
            </div>
          )}
        </div>

        {/* Preflight report */}
        {report && !deployResult && (
          <>
            <PreflightReportView manifest={manifest} target={target} report={report} payload={packagePayload} />
            <div className="mb-5 mt-5 flex items-center justify-end gap-3">
              {!report.ok && (
                <p className="mr-auto text-[13px] text-danger-600 dark:text-danger-400">
                  Deploy is blocked until every preflight check passes.
                </p>
              )}
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!report.ok || deploying}
                className="btn btn-primary"
              >
                <Rocket className="h-4 w-4" />
                Deploy Candidate
              </button>
            </div>
          </>
        )}

        {/* Deploy result */}
        {deployResult && (
          <div className="card mb-5 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-success-600 dark:text-success-400" />
              <div className="min-w-0">
                <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                  {deployResult.status === 'replaced'
                    ? 'Candidate deployed (previous build replaced)'
                    : deployResult.status === 'noop'
                      ? 'Already deployed, no changes made'
                      : 'Candidate deployed'}
                </h3>
                <p className="mt-1 text-[13px] text-gray-600 dark:text-gray-300">
                  {deployResult.study.title} ({deployResult.study.code}) in{' '}
                  {deployResult.target?.environment}. Audit reference: deployment record #
                  {deployResult.ledger_id}.
                </p>
                <p className="mt-1.5 break-all font-mono text-xs text-gray-500 dark:text-gray-400">
                  Candidate {deployResult.manifest?.candidate_id}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Post-deploy verification report (FECOA-1920) */}
        {openVerification && (
          <div className="mb-5">
            <VerificationReportView
              verification={openVerification.verification}
              onClose={() => setOpenVerification(null)}
            />
          </div>
        )}

        {/* Deployment history */}
        <div className="card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                Deployment history
              </h3>
              <span className="badge badge-gray tabular-nums">{history.length}</span>
            </div>
            <button
              onClick={loadHistory}
              className="btn btn-ghost btn-sm"
            >
              <RefreshCw className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {history.length === 0 ? (
            <p className="border-t border-gray-100 px-5 py-6 text-[13px] text-gray-500 dark:border-white/[0.06] dark:text-gray-400">
              No candidate builds have been deployed into this environment yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-y border-gray-100 dark:border-white/[0.06]">
                    <th className="th-cell">When</th>
                    <th className="th-cell">Study</th>
                    <th className="th-cell">Candidate</th>
                    <th className="th-cell">From</th>
                    <th className="th-cell">Status</th>
                    <th className="th-cell">Deployed by</th>
                    <th className="th-cell">Verification</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                  {history.map((row) => (
                    <tr key={row.id} className="tr-interactive">
                      <td className="td-cell whitespace-nowrap tabular-nums">
                        {formatTimestamp(row.deployed_at)}
                      </td>
                      <td className="td-cell">
                        <span className="font-medium text-gray-900 dark:text-gray-50">{row.study_title || row.study_code}</span>
                        <span className="ml-2 code-chip">{row.study_code}</span>
                      </td>
                      <td className="td-cell">
                        {row.candidate_label}
                        <span className="block font-mono text-xs text-gray-500 dark:text-gray-400">
                          {row.candidate_id.slice(0, 8)}…
                        </span>
                      </td>
                      <td className="td-cell">
                        {row.source_environment}
                      </td>
                      <td className="td-cell">
                        <span className={`badge badge-dot capitalize ${STATUS_BADGES[row.status] || STATUS_BADGES.noop}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="td-cell">
                        {row.deployed_by_email}
                      </td>
                      <td className="td-cell">
                        {row.status !== 'deployed' ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Not applicable
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            {row.latest_verification ? (
                              <button
                                type="button"
                                onClick={() => setOpenVerification({
                                  deploymentId: row.id,
                                  verification: row.latest_verification,
                                })}
                                className={`badge badge-dot ${VERIFICATION_BADGES[row.latest_verification.result] || VERIFICATION_BADGES.unverifiable}`}
                                title={row.latest_verification.detail}
                              >
                                {VERIFICATION_LABELS[row.latest_verification.result] || row.latest_verification.result}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Not verified
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => runVerification(row.id)}
                              disabled={verifyingId === row.id}
                              className="btn btn-ghost btn-sm h-7 px-2 text-xs text-primary-700 dark:text-primary-400"
                            >
                              {verifyingId === row.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <ShieldCheck className="h-3.5 w-3.5" />}
                              {verifyingId === row.id ? 'Verifying…' : 'Verify'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ConfirmDialog
          isOpen={confirmOpen}
          onClose={() => !deploying && setConfirmOpen(false)}
          onConfirm={handleDeploy}
          title="Deploy Candidate Build?"
          message={`This will deploy into the ${target?.environment || 'current'} environment:\n\nStudy: ${manifest?.study?.title} (${manifest?.study?.study_code})\nCandidate: ${manifest?.candidate_label}\nExported from: ${manifest?.source_environment}\n\nThe deployment is recorded in the audit trail under your account.`}
          confirmText="Deploy"
          type="warning"
          isLoading={deploying}
        />
    </div>
  );
};

export default StudyPromotionMain;
